import React from 'react';
import Axios from 'axios';

import GetCollectionTours from '@src/api/GetCollectionTours';
import { generateTourSummery } from '@src/factories';
import { TourSummery } from '@src/types';

import useAuthenticationTokens from '../../hooks/useAuthenticationTokens';

const limit = 10;
let cancel = Axios.CancelToken.source();

const useCollectionTours = (id?: string, initial?: TourSummery[]) => {
  const {
    tokens: { accessToken },
  } = useAuthenticationTokens();
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [data, setData] = React.useState<TourSummery[]>([]);

  React.useEffect(() => {
    if (initial && initial.length !== 0) {
      setData(initial);
      setOffset(initial.length + 1);
    }
  }, [initial]);

  const makeRequest = React.useCallback(
    async (newOffset: number): Promise<TourSummery[]> => {
      if (!id) {
        return [];
      }
      const response = await GetCollectionTours(
        accessToken,
        id,
        newOffset,
        limit,
        {
          cancelToken: cancel.token,
        }
      );
      return response.data.tours.map((object) =>
        generateTourSummery(object.tour)
      );
    },
    [accessToken, id]
  );

  const handleRefresh = React.useCallback(async () => {
    try {
      cancel.cancel('Canceling previous requests');
      cancel = Axios.CancelToken.source();
      setRefreshing(true);
      const newData = await makeRequest(0);
      setData(newData);
      setOffset(limit);
    } catch (e) {
      console.log(e);
    } finally {
      setRefreshing(false);
    }
  }, [makeRequest]);

  const handleEndReached = React.useCallback(async () => {
    try {
      if (loading) {
        return;
      }
      setLoading(true);
      const newData = await makeRequest(offset);
      setData([...data, ...newData]);
      setOffset(limit + offset);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [data, loading, makeRequest, offset]);

  React.useEffect(
    () => () => cancel.cancel('Get collection posts cancelled'),
    []
  );

  return React.useMemo(
    () => ({
      data,
      handleEndReached,
      handleRefresh,
      refreshing,
      loading,
    }),
    [data, handleEndReached, handleRefresh, loading, refreshing]
  );
};

export default useCollectionTours;
